// ===================================
// FLOATING CHAT - src/pages/FloatingChat.tsx
// ===================================
import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import { Send, Minimize2, Maximize2, X, MessageSquare } from "lucide-react";
import { Message } from "../types";
import AiChatIcon from "../assets/aiChatIcon.png";

// Extended message interface for floating chat with sources
interface FloatingChatMessage extends Message {
  sources?: Array<{
    documentName: string;
    similarity: number;
    preview: string;
  }>;
}

interface FloatingChatProps {
  onClose?: () => void;
}

const FloatingChat: React.FC<FloatingChatProps> = ({ onClose }) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<FloatingChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      initializeChat();
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const initializeChat = () => {
    if (messages.length === 0) {
      const welcomeMessage: FloatingChatMessage = {
        id: "floating-welcome",
        role: "assistant",
        content: `Hi ${user?.name}! I'm your quick AI assistant. How can I help you today?`,
        created_at: new Date().toISOString(),
      };
      setMessages([welcomeMessage]);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || isLoading) return;

    const userMessageContent = newMessage.trim();
    setNewMessage("");
    setIsLoading(true);

    const userMessage: FloatingChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content: userMessageContent,
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);

    try {
      // Use RAG if available, otherwise regular chat
      let response;
      try {
        response = await api.post("/search/rag", {
          query: userMessageContent,
          topK: 3,
          similarityThreshold: 0.1,
        });

        const aiMessage: FloatingChatMessage = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: response.data.data.answer,
          created_at: new Date().toISOString(),
          sources: response.data.data.sources
            .slice(0, 2)
            .map((source: any) => ({
              documentName: source.documentName,
              similarity: source.similarity,
              preview: source.chunkText.substring(0, 100) + "...",
            })),
        };

        setMessages((prev) => [...prev, aiMessage]);
      } catch (ragError) {
        // Fallback to regular chat
        response = await api.post("/chat/", {
          message: userMessageContent,
        });

        const aiMessage: FloatingChatMessage = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: response.data.message.content,
          created_at: response.data.message.created_at,
        };

        setMessages((prev) => [...prev, aiMessage]);
      }
    } catch (error) {
      const errorMessage: FloatingChatMessage = {
        id: (Date.now() + 2).toString(),
        role: "assistant",
        content: "I apologize, but I encountered an issue. Please try again.",
        created_at: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleChat = () => {
    if (isOpen) {
      setIsMinimized(!isMinimized);
    } else {
      setIsOpen(true);
      setIsMinimized(false);
    }
  };

  const closeChat = () => {
    setIsOpen(false);
    setIsMinimized(false);
    if (onClose) onClose();
  };

  const AvatarBot = () => (
    <div className="w-6 h-6 rounded-full bg-[#8b3dff] flex items-center justify-center text-white text-xs">
      🤖
    </div>
  );

  const AvatarUser = () => (
    <div className="w-6 h-6 rounded-full bg-[#1e293b] border border-[#334155] flex items-center justify-center text-white text-xs">
      {user?.name?.charAt(0).toUpperCase() || "👤"}
    </div>
  );

  const renderMessage = (message: FloatingChatMessage) => {
    const isUser = message.role === "user";
    const isWelcome = message.id === "floating-welcome";

    return (
      <div
        key={message.id}
        className={`flex items-start space-x-2 mb-4 ${
          isUser ? "flex-row-reverse space-x-reverse" : ""
        }`}
      >
        <div className="flex-shrink-0 mt-1">
          {isUser ? <AvatarUser /> : <AvatarBot />}
        </div>

        <div className={`max-w-[75%] ${isUser ? "text-right" : "text-left"}`}>
          <div
            className={`px-3 py-2 rounded-lg text-sm ${
              isUser
                ? "bg-[#8b3dff] text-white rounded-br-sm"
                : isWelcome
                ? "bg-[#020617] border border-[#334155] text-white rounded-bl-sm"
                : "bg-[#020617] border border-[#334155] text-white rounded-bl-sm"
            }`}
          >
            <div className="leading-relaxed">{message.content}</div>
          </div>

          {!isUser && message.sources && message.sources.length > 0 && (
            <div className="mt-1 text-xs text-gray-500">
              📚 Referenced {message.sources.length} source
              {message.sources.length !== 1 ? "s" : ""}
            </div>
          )}

          <div
            className={`text-xs mt-1 ${
              isUser ? "text-gray-400" : "text-gray-500"
            }`}
          >
            {new Date(message.created_at).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={toggleChat}
          className="w-16 h-16 bg-[#8b3dff] text-white rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 flex items-center justify-center text-2xl pulse-animation"
          title="Open chat assistant"
        >
          <img src={AiChatIcon} alt="chat icon" className="bot-image" />
        </button>
        <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold animate-pulse">
          !
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <div
        className={`bg-[#0f172a] rounded-2xl shadow-2xl border border-[#334155] transition-all duration-300 ${
          isMinimized ? "w-80 h-16" : "w-96 h-[600px]"
        }`}
      >
        {/* Header */}
        <div className="bg-[#8b3dff] text-white p-4 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AvatarBot />
            <div>
              <h3 className="font-semibold text-sm">Slavo AI</h3>
              <p className="text-xs opacity-90">Quick Assistant</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={toggleChat}
              className="w-6 h-6 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 flex items-center justify-center transition-colors duration-200"
              title={isMinimized ? "Expand" : "Minimize"}
            >
              {isMinimized ? (
                <Maximize2 className="w-3 h-3" />
              ) : (
                <Minimize2 className="w-3 h-3" />
              )}
            </button>
            <button
              onClick={closeChat}
              className="w-6 h-6 rounded-full bg-white bg-opacity-30 hover:bg-opacity-30 flex items-center justify-center transition-colors duration-200"
              title="Close chat"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Messages */}
            <div className="h-96 overflow-y-auto p-4 bg-[#0f172a]">
              {messages.map(renderMessage)}

              {isLoading && (
                <div className="flex items-start space-x-2 mb-4">
                  <div className="flex-shrink-0 mt-1">
                    <AvatarBot />
                  </div>
                  <div className="bg-[#020617] px-3 py-2 rounded-lg rounded-bl-sm text-sm">
                    <LoadingSpinner size="sm" message="Thinking..." />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-[#334155] bg-[#0f172a] rounded-b-2xl">
              <form onSubmit={handleSendMessage} className="flex space-x-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="bg-[#020617] flex-1 border border-[#334155] text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim() || isLoading}
                  className="bg-[#8b3dff] text-white p-2 rounded-lg  hover:bg-purple-700 disabled:opacity-50 transition-all duration-200"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>

              <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                <span>Powered by Slavo AI</span>
                <button
                  onClick={() => (window.location.href = "/chat")}
                  className="text-blue-600 hover:text-blue-800"
                >
                  Open full chat →
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FloatingChat;
